import styled from 'styled-components';

import CachedImage from 'inquiry/components/ImageRemote/CachedImage';
import { useApply } from 'inquiry/lib/jsonLogic';
import { sizeCss, StyleValueSizeConfig } from 'lib/inquiryStyles';
import { ImageRemoteComponent } from 'lib/interfaces/ui-components';
import { Inquiry } from 'verify/models/inquiry';

export const IMAGE_REMOTE_DIMENSION_DEFAULTS: {
  width: StyleValueSizeConfig;
  height: StyleValueSizeConfig;
} = {
  width: {
    unit: '%',
    value: 100,
  },
  height: {
    unit: '%',
    value: 100,
  },
};

const StyledCachedImage = styled(CachedImage)<{ styles: ImageRemoteComponent['styles'] }>(
  ({ styles, height, width }) => ({
    display: 'block',
    objectFit: 'contain',
    height: sizeCss(styles?.['height'].base.base) ?? height,
    width: sizeCss(styles?.['width'].base.base) ?? width,
    // Context: INC-427
    // 100vw is important here to keep ImageRemote used for custom assets
    // inside absolutely positioned containers from arbitrarily sizing up,
    // which can cause perf issues if the asset is animated.
    maxWidth: 'min(100%, 100vw)',
    alignSelf: styles?.['justify'].base.base,
    marginTop: sizeCss(styles?.margin.base.base?.top),
    marginBottom: sizeCss(styles?.margin.base.base?.bottom),
    marginLeft: sizeCss(styles?.margin.base.base?.left),
    marginRight: sizeCss(styles?.margin.base.base?.right),
  }),
);

interface ImageRemoteProps {
  component: ImageRemoteComponent;
  inquiry?: Inquiry;
  onLoad?: () => void;
  onError?: () => void;
  environment?: string;
}

const ImageRemote = ({ component, inquiry, onLoad, onError, environment }: ImageRemoteProps) => {
  const {
    attributes: { url, height, width, 'content-type': contentType, hidden, alt },
  } = component;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const apply = useApply(component.name, inquiry?.attributes['next-step'].name);
  const isHidden = apply<boolean>(hidden);

  if (isHidden) {
    return null;
  }

  return (
    <StyledCachedImage
      onLoad={onLoad}
      onError={onError}
      src={url}
      contentType={contentType}
      data-test={component.type}
      height={height ?? sizeCss(IMAGE_REMOTE_DIMENSION_DEFAULTS.height)}
      width={width ?? sizeCss(IMAGE_REMOTE_DIMENSION_DEFAULTS.width)}
      styles={component.styles}
      environment={environment}
      // If alt text is not provided, hide image from screen readers.
      // Support is not uniform -- some screen readers respect ARIA role,
      // while others require setting alt text to ''.
      alt={alt || ''}
      // aria-label, on the other hand, should NOT default to ''.
      aria-label={alt || undefined}
      role={alt ? undefined : 'presentation'}
    />
  );
};

export default ImageRemote;
